export interface OfficeHourLike {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export interface GroupedOfficeHours {
  label: string;
  shortLabel: string;
  hours: string;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

const DAY_LABELS: Record<(typeof DAY_ORDER)[number], string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

const DAY_SHORT_LABELS: Record<(typeof DAY_ORDER)[number], string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

function hasHourValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return (
    normalized !== '' &&
    normalized !== 'false' &&
    normalized !== 'null' &&
    normalized !== 'undefined'
  );
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function isCanonicalDay(key: string): key is (typeof DAY_ORDER)[number] {
  return (DAY_ORDER as readonly string[]).includes(key);
}

function isClosedDay(entry: OfficeHourLike): boolean {
  const open = entry.open_time?.trim().toLowerCase();
  const close = entry.close_time?.trim().toLowerCase();
  if (open === 'closed' || close === 'closed') return true;
  return !hasHourValue(entry.open_time) && !hasHourValue(entry.close_time);
}

/**
 * Ensures every weekday is present. PMS feeds often omit closed days
 * entirely; those missing days are filled in as Closed so hours lists
 * always read as a full week.
 *
 * Existing entries keep their order of first-seen weekday; unknown day
 * labels (e.g. "Holiday") are appended after the week.
 */
export function expandOfficeHoursWithClosedDays<T extends OfficeHourLike>(
  raw: T[],
): OfficeHourLike[] {
  const byDay = new Map<string, OfficeHourLike>();
  const extras: OfficeHourLike[] = [];

  for (const entry of raw) {
    if (!hasHourValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (isCanonicalDay(key)) {
      if (!byDay.has(key)) byDay.set(key, entry);
    } else {
      extras.push(entry);
    }
  }

  if (byDay.size === 0 && extras.length === 0) return [];

  const week = DAY_ORDER.map((key) => {
    return (
      byDay.get(key) ?? {
        day: DAY_LABELS[key],
        open_time: null,
        close_time: null,
      }
    );
  });

  return [...week, ...extras];
}

export function groupOfficeHours(
  raw: OfficeHourLike[],
  formatTime: (value: string) => string,
  options?: { timeJoiner?: string },
): GroupedOfficeHours[] {
  const timeJoiner = options?.timeJoiner ?? ' - ';
  const expanded = expandOfficeHoursWithClosedDays(raw);
  const groups: GroupedOfficeHours[] = [];

  for (const entry of expanded) {
    if (!hasHourValue(entry.day)) continue;

    const key = normalizeDay(entry.day);
    const dayLabel = isCanonicalDay(key) ? DAY_LABELS[key] : entry.day;
    const dayShortLabel = isCanonicalDay(key)
      ? DAY_SHORT_LABELS[key]
      : entry.day.slice(0, 3);
    const hoursStr = isClosedDay(entry)
      ? 'Closed'
      : `${formatTime(entry.open_time || '')}${timeJoiner}${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}
